import { safeStorage } from './safe-storage';

export interface MakeCollapsibleOptions {
  /** Initial open state if no persisted value exists. */
  initiallyOpen: boolean;
  /**
   * Optional storage key. If provided, the open/closed state is persisted
   * to localStorage under this key. Omit for no persistence.
   *
   * Convention: `'learnforge:collapsible:<demo>:<panel>'`, e.g.
   * `'learnforge:collapsible:3d-geometry:lines'`.
   */
  storageKey?: string;
  /**
   * CSS selector for the clickable title element. Defaults to '.panel-title'
   * (the .panel primitive); pass e.g. '.chart-card-header' for other cards.
   */
  titleSelector?: string;
}

export interface CollapsibleHandle {
  setOpen(open: boolean): void;
  isOpen(): boolean;
  destroy(): void;
}

/**
 * Makes a `.panel` element collapsible by clicking its `.panel-title`.
 *
 * The title is given ARIA attributes (role="button", tabindex="0",
 * aria-expanded) and a chevron indicator. A `data-collapsed="true|false"`
 * attribute on the panel itself drives the CSS that hides the body.
 * Pass `titleSelector` to collapse on a different element than `.panel-title`
 * (e.g. `.chart-card-header`).
 *
 * Throws if the panel has no `.panel-title` child.
 */
export function makeCollapsible(
  panelEl: HTMLElement,
  opts: MakeCollapsibleOptions,
): CollapsibleHandle {
  const titleSelector = opts.titleSelector ?? '.panel-title';
  const title = panelEl.querySelector<HTMLElement>(titleSelector);
  if (!title) {
    throw new Error(`makeCollapsible: panel has no ${titleSelector} child`);
  }

  const readPersisted = (): boolean | null => {
    if (!opts.storageKey) return null;
    const raw = safeStorage.get(opts.storageKey);
    if (raw === 'open') return true;
    if (raw === 'closed') return false;
    return null;
  };

  let open = readPersisted() ?? opts.initiallyOpen;

  title.setAttribute('role', 'button');
  title.setAttribute('tabindex', '0');
  title.classList.add('panel-title-collapsible');

  const apply = (): void => {
    panelEl.setAttribute('data-collapsed', open ? 'false' : 'true');
    title.setAttribute('aria-expanded', open ? 'true' : 'false');
  };
  apply();

  const toggle = (): void => {
    open = !open;
    apply();
    if (opts.storageKey) {
      safeStorage.set(opts.storageKey, open ? 'open' : 'closed');
    }
  };

  const onClick = (e: MouseEvent): void => {
    const target = e.target as HTMLElement | null;
    if (target && target !== title && target.closest('button, select, input, a')) return;
    toggle();
  };
  const onKeyDown = (e: KeyboardEvent): void => {
    const target = e.target as HTMLElement | null;
    if (target && target !== title && target.closest('button, select, input, a')) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  title.addEventListener('click', onClick);
  title.addEventListener('keydown', onKeyDown);

  return {
    setOpen(next): void {
      if (next === open) return;
      toggle();
    },
    isOpen(): boolean {
      return open;
    },
    destroy(): void {
      title.removeEventListener('click', onClick);
      title.removeEventListener('keydown', onKeyDown);
      title.removeAttribute('role');
      title.removeAttribute('tabindex');
      title.removeAttribute('aria-expanded');
      title.classList.remove('panel-title-collapsible');
      panelEl.removeAttribute('data-collapsed');
    },
  };
}
